/**
 * The InputSnapshot contract. This is the ONLY thing `src/combat` knows about input
 * (ARCHITECTURE.md §5). Devices, bindings, buffering and command parsing all resolve down to
 * one of these per player per tick.
 *
 * Written by `core-input` during `fixedUpdate`, before `combat-sim` runs. Combat treats it as
 * read-only. Three-free.
 *
 * Button scheme (5 attack buttons + guard), notated in CONTROLS.md:
 *   1 = LP  light punch      2 = HP  heavy punch
 *   3 = LK  light kick       4 = HK  heavy kick
 *   5 = SP  special / stance
 *   G = guard (holding back also guards)
 * Throw = 1+3. Command throw = motion + 2+4. Parry / Anchor = 5.
 */

import { INPUT_BUFFER_TICKS } from '../core/constants.js';

/** Ordered list of every digital input. Index order is stable and used for bitmasks. */
export const BUTTONS = Object.freeze(['lp', 'hp', 'lk', 'hk', 'sp', 'guard']);
export const DIRECTIONS = Object.freeze(['up', 'down', 'left', 'right']);
/** Facing-relative directions, derived from left/right and the fighter's facing. */
export const RELATIVE = Object.freeze(['fwd', 'back']);

export const ALL_INPUTS = Object.freeze([...DIRECTIONS, ...RELATIVE, ...BUTTONS]);

/** Bit positions for the compact per-tick mask stored in the buffer and in replays. */
export const BIT = Object.freeze({
  up: 1 << 0, down: 1 << 1, left: 1 << 2, right: 1 << 3,
  lp: 1 << 4, hp: 1 << 5, lk: 1 << 6, hk: 1 << 7,
  sp: 1 << 8, guard: 1 << 9,
  fwd: 1 << 10, back: 1 << 11,
});

/**
 * Numeric direction on the numpad convention, computed from up/down/fwd/back.
 * 5 = neutral, 6 = forward, 4 = back, 8 = up, 2 = down, 9 = up-forward, etc.
 */
export function numpadDirection(snap) {
  const h = snap.fwd ? 1 : snap.back ? -1 : 0;
  const v = snap.up ? 1 : snap.down ? -1 : 0;
  if (v > 0) return h > 0 ? 9 : h < 0 ? 7 : 8;
  if (v < 0) return h > 0 ? 3 : h < 0 ? 1 : 2;
  return h > 0 ? 6 : h < 0 ? 4 : 5;
}

/**
 * Creates a zeroed snapshot with every field present, so combat call sites stay monomorphic.
 * @param {number} playerIndex
 */
export function createInputSnapshot(playerIndex) {
  const snap = {
    player: playerIndex,
    device: 'keyboard',
    tick: 0,

    // --- current held state (screen-relative) ---
    up: false, down: false, left: false, right: false,
    // --- current held state (facing-relative; the ones combat actually reads) ---
    fwd: false, back: false,
    // --- buttons ---
    lp: false, hp: false, lk: false, hk: false, sp: false, guard: false,

    // --- edges this tick ---
    pressedUp: false, pressedDown: false, pressedLeft: false, pressedRight: false,
    pressedFwd: false, pressedBack: false,
    pressedLp: false, pressedHp: false, pressedLk: false, pressedHk: false,
    pressedSp: false, pressedGuard: false,
    releasedFwd: false, releasedBack: false,
    releasedLp: false, releasedHp: false, releasedLk: false, releasedHk: false,
    releasedSp: false, releasedGuard: false,

    // --- held durations, in ticks ---
    heldFwd: 0, heldBack: 0, heldUp: 0, heldDown: 0,
    heldLp: 0, heldHp: 0, heldLk: 0, heldHk: 0, heldSp: 0, heldGuard: 0,

    /** Numpad direction 1-9. */
    dir: 5,
    /** Bitmask of every held input this tick — what replays record. */
    mask: 0,

    /**
     * Motion command recognised on this tick, e.g. 'ff' (forward dash), 'bb' (backdash),
     * 'qcf', 'dp', 'ss-up', 'ss-down'. Empty string when none.
     */
    command: '',
    /** How many ticks ago the recognised command completed (0 = this tick). */
    commandAge: 0,

    /**
     * Ring buffer of the last INPUT_BUFFER_TICKS masks, newest last. Combat uses this for
     * negative-edge windows and for "was this button pressed within the buffer" checks
     * without needing the raw device history.
     */
    buffer: new Int32Array(INPUT_BUFFER_TICKS),
    bufferHead: 0,

    /** Set true while a replay or AI controller is driving this slot. */
    synthetic: false,
  };
  return snap;
}

/** Clears the per-tick edge flags. Held state and buffers survive. */
export function clearEdges(snap) {
  snap.pressedUp = false;
  snap.pressedDown = false;
  snap.pressedLeft = false;
  snap.pressedRight = false;
  snap.pressedFwd = false;
  snap.pressedBack = false;
  snap.pressedLp = false;
  snap.pressedHp = false;
  snap.pressedLk = false;
  snap.pressedHk = false;
  snap.pressedSp = false;
  snap.pressedGuard = false;
  snap.releasedFwd = false;
  snap.releasedBack = false;
  snap.releasedLp = false;
  snap.releasedHp = false;
  snap.releasedLk = false;
  snap.releasedHk = false;
  snap.releasedSp = false;
  snap.releasedGuard = false;
  snap.command = '';
  snap.commandAge = 0;
}

/** Zeroes everything, including held state. Used on round reset and focus loss. */
export function resetSnapshot(snap) {
  clearEdges(snap);
  snap.up = false;
  snap.down = false;
  snap.left = false;
  snap.right = false;
  snap.fwd = false;
  snap.back = false;
  snap.lp = false;
  snap.hp = false;
  snap.lk = false;
  snap.hk = false;
  snap.sp = false;
  snap.guard = false;
  snap.heldFwd = 0;
  snap.heldBack = 0;
  snap.heldUp = 0;
  snap.heldDown = 0;
  snap.heldLp = 0;
  snap.heldHp = 0;
  snap.heldLk = 0;
  snap.heldHk = 0;
  snap.heldSp = 0;
  snap.heldGuard = 0;
  snap.dir = 5;
  snap.mask = 0;
  snap.buffer.fill(0);
  snap.bufferHead = 0;
}

/** Packs the current held state into the tick mask and appends it to the ring buffer. */
export function packAndBuffer(snap) {
  let m = 0;
  if (snap.up) m |= BIT.up;
  if (snap.down) m |= BIT.down;
  if (snap.left) m |= BIT.left;
  if (snap.right) m |= BIT.right;
  if (snap.lp) m |= BIT.lp;
  if (snap.hp) m |= BIT.hp;
  if (snap.lk) m |= BIT.lk;
  if (snap.hk) m |= BIT.hk;
  if (snap.sp) m |= BIT.sp;
  if (snap.guard) m |= BIT.guard;
  if (snap.fwd) m |= BIT.fwd;
  if (snap.back) m |= BIT.back;
  snap.mask = m;
  snap.buffer[snap.bufferHead] = m;
  snap.bufferHead = (snap.bufferHead + 1) % snap.buffer.length;
  snap.dir = numpadDirection(snap);
  return m;
}

/**
 * Was `bit` newly pressed within the last `withinTicks`? This is the input-buffer lookup
 * combat uses so a move entered a few ticks early still comes out — the difference between
 * "responsive" and "eats my inputs".
 */
export function pressedWithin(snap, bit, withinTicks) {
  const n = snap.buffer.length;
  const limit = Math.min(withinTicks, n - 1);
  for (let i = 0; i <= limit; i++) {
    const cur = snap.buffer[(snap.bufferHead - 1 - i + n * 2) % n];
    const prev = snap.buffer[(snap.bufferHead - 2 - i + n * 2) % n];
    if ((cur & bit) !== 0 && (prev & bit) === 0) return true;
  }
  return false;
}

/** True if `bit` was held on every tick of the last `ticks` ticks. */
export function heldFor(snap, bit, ticks) {
  const n = snap.buffer.length;
  const limit = Math.min(ticks, n);
  for (let i = 0; i < limit; i++) {
    if ((snap.buffer[(snap.bufferHead - 1 - i + n * 2) % n] & bit) === 0) return false;
  }
  return true;
}

/** Human-readable input string for training-mode input history, e.g. "6 + HP". */
export function describeSnapshot(snap) {
  const parts = [];
  if (snap.dir !== 5) parts.push(String(snap.dir));
  if (snap.lp) parts.push('LP');
  if (snap.hp) parts.push('HP');
  if (snap.lk) parts.push('LK');
  if (snap.hk) parts.push('HK');
  if (snap.sp) parts.push('SP');
  if (snap.guard) parts.push('G');
  return parts.length ? parts.join('+') : '—';
}
